(() => {
  const ROOT = '#CollectionProductGrid';
  const TITLE_SELECTOR = '.card-title[data-product-title]';
  const activeFilters = new Map();

  const FILTER_GROUPS = [
    {
      key: 'lighting',
      label: 'Lighting',
      values: [
        ['backlit', 'Backlit'],
        ['front-lit', 'Front-lit'],
        ['front-backlit', 'Front + backlit']
      ]
    },
    {
      key: 'control',
      label: 'Control',
      values: [
        ['sensor-switch', 'Motion sensor + switch'],
        ['sensor-only', 'Motion sensor only']
      ]
    },
    {
      key: 'shape',
      label: 'Shape',
      values: [
        ['round', 'Round'],
        ['arch', 'Arch'],
        ['pill-oval', 'Pill / oval'],
        ['rectangle', 'Rectangle'],
        ['oval', 'Oval'],
        ['half-moon', 'Half moon'],
        ['moon', 'Moon'],
        ['leaf', 'Leaf'],
        ['fan', 'Fan'],
        ['pear', 'Pear'],
        ['irregular', 'Irregular']
      ]
    },
    {
      key: 'frame',
      label: 'Frame',
      values: [
        ['frameless', 'Frameless'],
        ['black-frame', 'Black frame']
      ]
    },
    {
      key: 'storage',
      label: 'Storage',
      values: [
        ['with-storage', 'With storage'],
        ['no-storage', 'No storage']
      ]
    },
    {
      key: 'features',
      label: 'Extras',
      values: [
        ['shelf', 'Shelf'],
        ['magnifier', 'Magnifier'],
        ['hook', 'Hook']
      ]
    }
  ];

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const parseSizeMm = (raw) => {
    const match = raw.match(/^(\d+(?:[x*×]\d+){0,2})\s*(mm|cm)?/i);
    if (!match) return '';

    const unit = (match[2] || 'mm').toLowerCase();
    const multiplier = unit === 'cm' ? 10 : 1;
    const values = match[1]
      .split(/[x*×]/i)
      .map((value) => Number(value) * multiplier)
      .map((value) => Number.isInteger(value) ? String(value) : String(value));

    const isDiameter = /\bDia\b/i.test(raw);
    return `${isDiameter ? 'Ø' : ''}${values.join('×')} mm`;
  };

  const getLighting = (raw) => {
    if (/front[- ]?and[- ]?back|front.*backlit/i.test(raw)) {
      return { key: 'front-backlit', label: 'FRONT + BACKLIT' };
    }
    if (/front[- ]?lit/i.test(raw)) return { key: 'front-lit', label: 'FRONT-LIT' };
    if (/backlit/i.test(raw)) return { key: 'backlit', label: 'BACKLIT' };
    return { key: '', label: '' };
  };

  const getControl = (raw) => {
    if (!/motion sensor/i.test(raw)) return { key: '', label: '' };
    return {
      key: /switch/i.test(raw) ? 'sensor-switch' : 'sensor-only',
      label: 'MOTION SENSOR'
    };
  };

  const getShape = (raw) => {
    const rules = [
      [/Pill Shape Oval|Pill Oval Shape|Pill Shape/i, 'pill-oval'],
      [/Irregular Pear Shape|Pear Shape/i, 'pear'],
      [/Half Moon Shape/i, 'half-moon'],
      [/\bMoon Shape\b/i, 'moon'],
      [/Fan Shape/i, 'fan'],
      [/Leaf Shape Narrow|Leaf Shape/i, 'leaf'],
      [/\bArch\b(?: Shape)?/i, 'arch'],
      [/\bOval\b/i, 'oval'],
      [/\bRound\b/i, 'round'],
      [/\bRectangle\b/i, 'rectangle'],
      [/\bIrregular Shape\b/i, 'irregular']
    ];
    for (const [pattern, key] of rules) {
      if (pattern.test(raw)) return key;
    }
    return '';
  };

  const getFrame = (raw) => {
    if (/Matt Black Frame|Black Frame/i.test(raw)) return 'black-frame';
    if (/Frameless/i.test(raw)) return 'frameless';
    return '';
  };

  const getStorage = (raw) => {
    return /Recessed Mirror Cabinet|Mirror Cabinet|Led Mirror Cabinet/i.test(raw)
      ? 'with-storage'
      : 'no-storage';
  };

  const getFeatures = (raw) => {
    const features = [];
    if (/With Shelf/i.test(raw)) features.push('shelf');
    if (/Magnifier/i.test(raw)) features.push('magnifier');
    if (/Gold Hook|Black Hook|\bHook\b/i.test(raw)) features.push('hook');
    return features;
  };

  const buildMeta = (rawTitle) => {
    const raw = normalize(rawTitle);
    if (!raw || !/\bmirror\b/i.test(raw)) return null;

    const lighting = getLighting(raw);
    const control = getControl(raw);
    const storage = getStorage(raw);
    const features = getFeatures(raw);
    const details = [lighting.label, control.label];

    if (storage === 'with-storage') details.push('STORAGE');
    if (features.includes('shelf')) details.push('SHELF');
    if (features.includes('magnifier')) details.push('MAGNIFIER');
    if (features.includes('hook')) details.push('HOOK');

    return {
      raw,
      size: parseSizeMm(raw) || 'LED MIRROR',
      lighting: lighting.key,
      control: control.key,
      shape: getShape(raw),
      frame: getFrame(raw),
      storage,
      features,
      details: details.filter(Boolean)
    };
  };

  const enhanceCard = (link) => {
    if (!link) return;

    const rawTitle = normalize(link.getAttribute('data-product-title') || link.getAttribute('aria-label') || link.textContent);
    const meta = buildMeta(rawTitle);
    if (!meta) return;

    const product = link.closest('li.product');
    if (product) product._ozbLedMeta = meta;

    if (link.dataset.ozbLedParamTitle === 'true') return;

    const text = link.querySelector(':scope > .text');
    if (!text) return;
    text.textContent = '';

    const line = document.createElement('span');
    line.className = 'ozb-led-param-title__line';

    const size = document.createElement('span');
    size.className = 'ozb-led-param-title__size';
    size.textContent = meta.size;
    line.appendChild(size);

    if (meta.details.length) {
      const details = document.createElement('span');
      details.className = 'ozb-led-param-title__details';
      details.textContent = meta.details.join(' · ');
      line.appendChild(details);
    }

    text.appendChild(line);
    link.classList.add('ozb-led-param-title');
    link.setAttribute('aria-label', meta.raw);
    link.dataset.ozbLedParamTitle = 'true';
  };

  const getProducts = () => {
    const scope = document.querySelector(ROOT);
    if (!scope) return [];
    scope.querySelectorAll(TITLE_SELECTOR).forEach(enhanceCard);
    return [...scope.querySelectorAll('li.product')];
  };

  const metaMatches = (meta, group, value) => {
    if (!meta) return false;
    if (group === 'features') return meta.features.includes(value);
    return meta[group] === value;
  };

  const countValue = (products, group, value) => {
    return products.reduce((count, product) => count + (metaMatches(product._ozbLedMeta, group, value) ? 1 : 0), 0);
  };

  const syncFilterControlState = (input) => {
    const label = input?.labels?.[0];
    if (!label) return;

    label.classList.toggle('is-selected', input.checked);
    label.setAttribute('aria-checked', String(input.checked));
  };

  const toggleFilterInput = (input) => {
    if (!input || input.disabled) return;
    input.checked = !input.checked;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const renderSmartFilters = (products) => {
    const sidebar = document.querySelector('.page-sidebar .halo-sidebar-wrapper');
    const form = sidebar?.querySelector('#CollectionFiltersForm');
    if (!sidebar || !form) return;

    let container = sidebar.querySelector('.ozb-led-smart-filters');
    if (!container) {
      container = document.createElement('div');
      container.className = 'ozb-led-smart-filters';
      container.setAttribute('aria-label', 'LED mirror filters');
      const facets = form.closest('.facets');
      const collectionFilters = facets?.parentElement;
      if (facets && collectionFilters) collectionFilters.insertBefore(container, facets);
      else sidebar.appendChild(container);
    }

    const availableGroups = FILTER_GROUPS
      .map((group) => ({
        ...group,
        values: group.values
          .map(([value, label]) => ({ value, label, count: countValue(products, group.key, value) }))
          .filter((item) => item.count > 0)
      }))
      .filter((group) => group.values.length);

    /*
     * The observer below watches the product grid because Shopify replaces it
     * after a native facet change. Do not rebuild this container for every
     * refresh: rebuilding it on each mutation made the labels race with a
     * user's click, which is why some filters appeared unresponsive.
     */
    const signature = JSON.stringify(availableGroups.map((group) => [
      group.key,
      group.values.map(({ value, count }) => [value, count])
    ]));
    if (container.dataset.renderSignature === signature) return;
    container.dataset.renderSignature = signature;

    container.textContent = '';

    const status = document.createElement('div');
    status.className = 'ozb-led-filter-status';
    status.innerHTML = `
      <span class="ozb-led-filter-status__label">Mirror filters</span>
      <span class="ozb-led-filter-status__count" data-ozb-led-filter-count aria-live="polite"></span>
      <button type="button" class="ozb-led-filter-clear">Clear</button>
    `;
    container.appendChild(status);

    availableGroups.forEach((group) => {
      const block = document.createElement('div');
      block.className = 'sidebarBlock ozb-led-smart-filter';
      block.dataset.ozbLedFilterGroup = group.key;

      const heading = document.createElement('h3');
      heading.className = 'sidebarBlock-heading';
      heading.innerHTML = `<span class="facets__text">${group.label}</span>`;
      block.appendChild(heading);

      const content = document.createElement('div');
      content.className = 'sidebarBlock-content sidebarBlock-content--full facets__display';

      const list = document.createElement('ul');
      list.className = 'facets__list list-unstyled';
      list.setAttribute('role', 'list');

      group.values.forEach(({ value, label, count }) => {
        const item = document.createElement('li');
        item.className = 'list-menu__item facets__item';

        const id = `OzbLedFilter-${group.key}-${value}`;
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.value = value;
        input.dataset.ozbLedFilter = 'true';
        input.dataset.group = group.key;
        input.checked = activeFilters.get(group.key)?.has(value) || false;
        input.setAttribute('aria-label', `${label} filter`);

        const labelNode = document.createElement('label');
        labelNode.className = 'facet-checkbox form-label--checkbox';
        labelNode.htmlFor = id;
        labelNode.dataset.ozbLedFilterLabel = 'true';
        labelNode.tabIndex = 0;
        labelNode.setAttribute('role', 'checkbox');
        syncFilterControlState(input);
        labelNode.append(document.createTextNode(label));

        const countNode = document.createElement('span');
        countNode.className = 'count';
        countNode.textContent = `(${count})`;
        labelNode.appendChild(countNode);

        item.append(input, labelNode);
        list.appendChild(item);
      });

      content.appendChild(list);
      block.appendChild(content);
      container.appendChild(block);
    });
  };

  const applyFilters = (products) => {
    let visible = 0;

    products.forEach((product) => {
      const meta = product._ozbLedMeta;
      let matches = true;

      activeFilters.forEach((values, group) => {
        if (!values.size) return;
        const groupMatch = [...values].some((value) => metaMatches(meta, group, value));
        if (!groupMatch) matches = false;
      });

      product.classList.toggle('ozb-led-filter-hidden', !matches);
      if (matches) visible += 1;
    });

    document.querySelectorAll('.ozb-led-filter-status').forEach((status) => {
      status.dataset.visible = String(visible);
      status.dataset.total = String(products.length);
      const count = status.querySelector('[data-ozb-led-filter-count]');
      if (count) {
        count.textContent = visible === products.length
          ? `${products.length} mirrors`
          : `${visible} of ${products.length} mirrors`;
      }
    });
  };

  const refresh = () => {
    const products = getProducts();
    if (!products.length) return;
    renderSmartFilters(products);
    applyFilters(products);
  };

  let refreshFrame = 0;
  const scheduleRefresh = () => {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = 0;
      refresh();
    });
  };

  const onChange = (event) => {
    const input = event.target.closest?.('[data-ozb-led-filter="true"]');
    if (!input) return;

    const group = input.dataset.group;
    if (!activeFilters.has(group)) activeFilters.set(group, new Set());
    const values = activeFilters.get(group);
    if (input.checked) values.add(input.value);
    else values.delete(input.value);
    if (!values.size) activeFilters.delete(group);

    syncFilterControlState(input);
    applyFilters(getProducts());
  };

  const onClick = (event) => {
    const clear = event.target.closest?.('.ozb-led-filter-clear');
    if (clear) {
      event.preventDefault();
      activeFilters.clear();
      document.querySelectorAll('[data-ozb-led-filter="true"]').forEach((input) => {
        input.checked = false;
        syncFilterControlState(input);
      });
      applyFilters(getProducts());
      return;
    }

    const label = event.target.closest?.('label[data-ozb-led-filter-label="true"]');
    if (!label) return;

    const input = document.getElementById(label.htmlFor);
    if (!input?.matches?.('[data-ozb-led-filter="true"]')) return;

    /*
     * The theme hides checkbox inputs and paints the label as the control.
     * Toggle it ourselves so clicks on the text, count, or checkbox outline
     * all use the same reliable path.
     */
    event.preventDefault();
    event.stopPropagation();
    toggleFilterInput(input);
  };

  const onKeyDown = (event) => {
    const label = event.target.closest?.('label[data-ozb-led-filter-label="true"]');
    if (!label || !['Enter', ' '].includes(event.key)) return;

    const input = document.getElementById(label.htmlFor);
    if (!input?.matches?.('[data-ozb-led-filter="true"]')) return;

    event.preventDefault();
    event.stopPropagation();
    toggleFilterInput(input);
  };

  const start = () => {
    refresh();
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
        scheduleRefresh();
      }
    });
    const productCollection = document.querySelector(`${ROOT} .collection`) || document.querySelector(ROOT);
    if (productCollection) observer.observe(productCollection, { childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
